import axios from 'axios';
import { tokenStorage, clearAuthStorage } from '../utils/auth';
import { errorReportingService } from './errorReportingService';
import { getBackendUrl } from '../utils/https';

const API_BASE_URL = getBackendUrl();

class ApiService {
  private api: any;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config: any) => {
        const token = tokenStorage.get();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors and logging
    this.api.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        // Handle authentication errors - only redirect for non-auth endpoints
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          // Don't redirect on auth endpoints (login, register, superuser creation)
          if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/create-superuser')) {
            clearAuthStorage();
            window.location.href = '/login';
          }
        }

        // Report network errors
        if (error.config) {
          errorReportingService.reportNetworkError({
            url: error.config.url || '',
            method: error.config.method?.toUpperCase() || 'UNKNOWN',
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            requestData: error.config.data,
          });
        }

        return Promise.reject(error);
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public get<T = any>(url: string, config?: any): Promise<any> {
    return this.api.get(url, config);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public post<T = any>(url: string, data?: any, config?: any): Promise<any> {
    return this.api.post(url, data, config);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public put<T = any>(url: string, data?: any, config?: any): Promise<any> {
    return this.api.put(url, data, config);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public patch<T = any>(url: string, data?: any, config?: any): Promise<any> {
    return this.api.patch(url, data, config);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public delete<T = any>(url: string, config?: any): Promise<any> {
    return this.api.delete(url, config);
  }
}

export const apiService = new ApiService();
